import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Download, RefreshCw, Search, Trash2, AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { da } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";

interface TranslatedDocument {
  id: string;
  file_name: string;
  file_path: string;
  target_language: string;
  source_language?: string;
  status: string;
  created_at: string;
  tender_documents?: {
    file_name: string;
  };
  source_document_id?: string;
}

export const TranslatedDocuments = () => {
  const { tenderId } = useParams<{ tenderId: string }>();
  const { toast } = useToast();
  const { t } = useTranslation('offerManagement');
  const { currentLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<null | { id: string, filePath: string }>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: translatedDocuments = [],
    isLoading,
    refetch
  } = useQuery<TranslatedDocument[]>({
    queryKey: ['translated-documents', tenderId],
    queryFn: async () => {
      if (!tenderId) return [];

      const { data, error } = await supabase
        .from('translated_documents')
        .select('*, tender_documents(file_name)')
        .eq('tender_id', tenderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: Boolean(tenderId),
    refetchInterval: 15000
  });

  useEffect(() => {
    const handleTranslationCompleted = () => {
      console.log("Translation completed event received, refreshing list");
      refetch();
    };
    
    window.addEventListener('translation-completed', handleTranslationCompleted);
    
    return () => {
      window.removeEventListener('translation-completed', handleTranslationCompleted);
    };
  }, [refetch]);

  useEffect(() => {
    const checkProcessingDocuments = async () => {
      const processingDocs = translatedDocuments.filter(doc => doc.status === 'processing');
      
      if (processingDocs.length > 0) {
        console.log(`Found ${processingDocs.length} documents in processing status, triggering status check`);
        
        try {
          await supabase.functions.invoke("check-translation-status");
          setTimeout(() => {
            refetch();
          }, 2000);
        } catch (error) {
          console.error("Error triggering initial status check:", error);
        }
      }
    };
    
    if (translatedDocuments.length > 0) {
      checkProcessingDocuments();
    }
  }, [translatedDocuments, refetch]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await supabase.functions.invoke("check-translation-status");
      setTimeout(() => {
        refetch();
        setIsRefreshing(false);
      }, 2000);

      toast({
        title: t("translations.statusCheck"),
        description: t("translations.statusCheckDesc")
      });
    } catch (error) {
      console.error("Error triggering status check:", error);
      toast({
        title: t("translations.statusCheckFailed"),
        description: t("translations.statusCheckFailedDesc"),
        variant: "destructive"
      });
      setIsRefreshing(false);
    }
  };

  const handleDownload = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('translated_documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: t("common.success"),
        description: t("translations.downloadSuccess", { fileName })
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: t("errorMessages.downloadFailed"),
        description: t("errorMessages.downloadFailedDesc"),
        variant: "destructive"
      });
    }
  };

  const confirmDelete = (id: string, filePath: string) => {
    setDocumentToDelete({ id, filePath });
    setIsConfirmDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;
    
    try {
      const { error: storageError } = await supabase.storage
        .from('translated_documents')
        .remove([documentToDelete.filePath]);

      if (storageError) {
        console.error('Storage error:', storageError);
      }

      const { error: dbError } = await supabase
        .from('translated_documents')
        .delete()
        .eq('id', documentToDelete.id);

      if (dbError) throw dbError;

      toast({
        title: t("common.success"),
        description: t("translations.deleteSuccess"),
      });
      
      setIsConfirmDeleteOpen(false);
      setDocumentToDelete(null);
      
      refetch();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: t("errorMessages.deleteFailed"),
        description: t("errorMessages.deleteFailedDesc"),
        variant: "destructive"
      });
    }
  };

  const filteredDocuments = translatedDocuments.filter(doc => 
    doc.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.target_language.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (doc.source_language?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const getStatusDisplay = (status: string, createdAt: string) => {
    if (status === 'processing') {
      const createdTime = new Date(createdAt);
      const now = new Date();
      const minutesPassed = Math.floor((now.getTime() - createdTime.getTime()) / (1000 * 60));
      
      if (minutesPassed > 30) {
        return (
          <div className="flex items-center gap-2 text-amber-500">
            <AlertCircle className="h-4 w-4" />
            <span>{t("translations.processingDelayed")}</span>
          </div>
        );
      }
      return t("translations.processing") + "...";
    }
    if (status === 'completed') return t("translations.completed");
    if (status === 'failed') return t("translations.failed");
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getLanguageDisplay = (langCode: string) => {
    const languages: Record<string, string> = {
      'EN': 'English',
      'ES': 'Spanish',
      'FR': 'French',
      'DE': 'German',
      'IT': 'Italian',
      'PT': 'Portuguese',
      'NL': 'Dutch',
      'DA': 'Dansk',
      'FI': 'Finsk',
      'SV': 'Svensk',
      'PL': 'Polsk',
      'CS': 'Tjekkisk',
      'RO': 'Rumænsk',
      'HU': 'Ungarsk',
      'BG': 'Bulgarsk',
      'EL': 'Græsk',
      'SK': 'Slovakisk',
      'SL': 'Slovensk',
      'ET': 'Estisk',
      'LV': 'Lettisk',
      'LT': 'Litauisk',
      'MT': 'Maltesisk'
    };
    
    return languages[langCode] || langCode;
  };

  if (!tenderId) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t("translations.title")}</CardTitle>
            <CardDescription>
              {t("translations.description")}
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            title={t("translations.refreshStatus")}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("translations.searchTranslations")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <Alert variant="default">
              <AlertDescription>
                {searchQuery 
                  ? t("translations.noTranslationsMatch")
                  : t("translations.noTranslations")}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("documents.fileName")}</TableHead>
                    <TableHead>{t("translations.translatedTo")}</TableHead>
                    <TableHead>{t("translations.status")}</TableHead>
                    <TableHead>{t("translations.created")}</TableHead>
                    <TableHead className="text-right">{t("documents.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.file_name}</TableCell>
                      <TableCell>{getLanguageDisplay(doc.target_language)}</TableCell>
                      <TableCell>{getStatusDisplay(doc.status, doc.created_at)}</TableCell>
                      <TableCell>{formatDistanceToNow(new Date(doc.created_at), { 
                        addSuffix: true,
                        locale: currentLanguage === 'da' ? da : undefined
                      })}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {doc.status === 'completed' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(doc.file_path, doc.file_name)}
                              title={t("documents.download")}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => confirmDelete(doc.id, doc.file_path)}
                            title={t("documents.delete")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <Dialog open={isConfirmDeleteOpen} onOpenChange={setIsConfirmDeleteOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("translations.deleteTranslation")}</DialogTitle>
              <DialogDescription>
                {t("translations.deleteConfirm")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsConfirmDeleteOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                {t("common.delete")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
